import React from 'react'
import { hashHistory } from 'react-router'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import DropzoneS3Uploader from 'react-dropzone-s3-uploader'
import {
  addYears,
  formatDateLocale,
  formatPepMatchScore
} from '../../../../../lib/utils.es6'
import {
  getFullName,
  getMedicalConditions,
  getScheduleEntryMedicalConditions,
  isMedicalConditionDeclaredForScheduleEntry,
  describeMedicalConditions,
  describeScheduleEntryMedicalConditions
} from '../../../../../lib/personDetails.es6'
import {
  readPolicyField,
  readMemberField,
  policyDetailSources,
  policyStatusLabel
} from '../../../../../lib/policyDetails.es6'
import {
  getPartnerGuid,
  getUserFullname,
  hasAuthourity
} from '../../../../../lib/access.es6'
import { isOmkAgent } from '../../../../OldMutualKenya/shared/agentView'
import {
  updatePoliciesDcp,
  getS3BucketSignedUrlForPolicyDcpDocuments
} from '../../../../../lib/requests.es6'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import ProgressState from './Components/PolicyDocuments/ProgressState'
import {
  SUPPORTING_DOCUMENT_LABELS,
  findSupportingDocument,
  missingDecisionRequirements
} from '../../lib/flaggedPolicyDecision'
import useProductCurrency from '../../../../../hooks/useProductCurrency'

const ITEM_HEIGHT = 48

// Chip colour per customer risk rating, keyed lowercase so a rating of
// "Medium" or "medium" reads the same. Anything unrecognised stays neutral
// rather than being coloured as if it were understood.
const RISK_RATING_COLORS = {
  low: 'success',
  medium: 'warning',
  high: 'error'
}

// A risk score of 0 is a real value, so emptiness cannot be a falsy check.
const isPresent = value => value !== undefined && value !== null && value !== ''

// "country_of_citizenship" / "businessProduct" -> "Country of citizenship" /
// "Business product". The component keys are engine-side names, so they are
// humanised for display rather than listed raw.
const riskComponentLabel = key =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, s => s.toUpperCase())

const PolicyDetailsFlp = props => {
  const [takeActionDialogOpen, setTakeActionDialogOpen] = React.useState(false)
  const [reason, setReason] = React.useState('')
  const [loadingAction, setLoadingAction] = React.useState(false)
  const [feedback, setFeedback] = React.useState('')
  const [uploadedFile, setUploadedFile] = React.useState(null)
  const [uploadProgress, setUploadProgress] = React.useState(0)
  const [uploadError, setUploadError] = React.useState(null)
  const [s3Url] = React.useState(
    'https://policy-onboarding-documents.s3.amazonaws.com'
  )
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [selectedPolicyAction, setSelectedPolicyAction] = React.useState(null)
  // Which flag the Take Action dialog is operating on: 'aml' or 'pep'.
  const [actionType, setActionType] = React.useState('aml')

  const partnerGuid = getPartnerGuid()
  // A selling agent sees the policy, not the levers on it. Amend, beneficiary
  // updates and cancellation are back-office work; the beneficiary and
  // additional-details panels are the customer's data, not the agent's.
  const isAgentView = isOmkAgent()

  const open = Boolean(anchorEl)

  const policy = props.policy
  const ad = policy.additional_details || {}
  const nok = ad.nextOfKin || {}

  // When the policy runs the sequenced maker-checker flow, AML/PEP are decided
  // as maker-check stages (see MakerChecks). The legacy "Take Action" buttons
  // here write status directly via updatePoliciesDcp, which would bypass the
  // remaining maker_checks and wrongly activate the policy — so hide them and
  // keep only the informational AML/PEP display for maker_checks policies.
  const useMakerChecks =
    Array.isArray(policy.maker_checks) && policy.maker_checks.length > 0
  const mainMember = ad.mainMember || {}

  const currencyCode = useProductCurrency(policy.product_code)

  // A decision on a flagged policy is only allowed once the reviewer has given
  // a reason and attached a supporting document.
  const supportingDocumentLabel = SUPPORTING_DOCUMENT_LABELS[actionType]
  const existingSupportingDocument = findSupportingDocument(policy, actionType)
  const hasSupportingDocument = !!uploadedFile || !!existingSupportingDocument
  const trimmedReason = reason.trim()
  const missingRequirements = missingDecisionRequirements(
    reason,
    hasSupportingDocument
  )
  const canDecide = missingRequirements.length === 0

  const handleClick = (event, policy) => {
    setAnchorEl(event.currentTarget)
    setSelectedPolicyAction(policy)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // Helper to convert camelCase to readable text
  const camelToReadable = str => {
    if (!str) return 'N/A'
    return str
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, s => s.toUpperCase())
  }

  const takeAction = type => {
    setActionType(type === 'pep' ? 'pep' : 'aml')
    setTakeActionDialogOpen(true)
  }

  const handleDialogClose = () => {
    setTakeActionDialogOpen(false)
    setReason('')
    setFeedback('')
    setLoadingAction(false)
    setUploadedFile(null)
    setUploadProgress(0)
    setUploadError(null)
    if (feedback && !feedback.includes('error')) {
      setTimeout(() => {
        window.location.reload()
      }, 200)
    }
  }

  const handleFileUpload = info => {
    const payload = {
      policy_guid: props.policy.guid,
      description: supportingDocumentLabel,
      filename: supportingDocumentLabel,
      url: info.fileUrl.replace('undefined', info.bucketKey)
    }

    if (props.DocumentUpload) {
      props.DocumentUpload(payload)
    }

    setUploadedFile({
      filename: info.filename,
      url: info.fileUrl.replace('undefined', info.bucketKey)
    })
    setUploadProgress(100)
  }

  const getS3Url = (file, callback) =>
    getS3BucketSignedUrlForPolicyDcpDocuments(props.policy.guid, file, callback)

  const uploadOptions = {
    getSignedUrl: getS3Url,
    s3Url,
    uploadRequestHeaders: {},
    onProgress: progress => setUploadProgress(progress),
    onError: errorMessage => setUploadError(errorMessage)
  }

  const handleDecision = async action => {
    if (!props.policy || !props.policy.additional_details) return
    // Both the reason and the supporting document are mandatory; the buttons
    // are disabled without them, this is the backstop.
    if (!canDecide) return
    setLoadingAction(true)
    setFeedback('')
    const updatedAd = { ...props.policy.additional_details }
    const now = new Date().toISOString()
    // Operate on the aml_* or pep_* fields depending on which flag is being actioned.
    const prefix = actionType === 'pep' ? 'pep' : 'aml'
    if (action === 'Approve') {
      updatedAd[`${prefix}_flagged`] = false
      updatedAd[`${prefix}_action`] = 'approved'
      updatedAd[`${prefix}_approved_by`] = getUserFullname()
      updatedAd[`${prefix}_approved_at`] = now
      updatedAd[`${prefix}_reason`] = trimmedReason
      delete updatedAd[`${prefix}_rejected_by`]
      delete updatedAd[`${prefix}_rejected_at`]
    } else if (action === 'Reject') {
      updatedAd[`${prefix}_flagged`] = true
      updatedAd[`${prefix}_action`] = 'rejected'
      updatedAd[`${prefix}_rejected_by`] = getUserFullname()
      updatedAd[`${prefix}_rejected_at`] = now
      updatedAd[`${prefix}_reason`] = trimmedReason
      delete updatedAd[`${prefix}_approved_by`]
      delete updatedAd[`${prefix}_approved_at`]
    }
    try {
      await updatePoliciesDcp({
        additional_details: updatedAd,
        policy_guid: props.policy.guid,
        status:
          action === 'Approve'
            ? 'Unpaid'
            : action === 'Reject'
            ? 'Rejected'
            : undefined
      })
      setFeedback(
        action === 'Approve'
          ? 'Policy successfully approved.'
          : 'Policy rejected.'
      )
      setLoadingAction(false)
    } catch (e) {
      setLoadingAction(false)
      setFeedback('An error occurred. Please try again.')
    }
  }

  const getAmlStatusChip = () => {
    if (ad.aml_action === 'approved') {
      return (
        <Chip
          label="AML Approved"
          color="success"
          size="small"
          variant="outlined"
        />
      )
    }
    if (ad.aml_action === 'rejected') {
      return (
        <Chip
          label="AML Rejected"
          color="error"
          size="small"
          variant="outlined"
        />
      )
    }
    if (ad.aml_flagged) {
      return (
        <Chip
          label="AML Flagged"
          color="warning"
          size="small"
          variant="outlined"
        />
      )
    }
    return null
  }

  const getPepStatusChip = () => {
    if (ad.pep_action === 'approved') {
      return (
        <Chip
          label="PEP Approved"
          color="success"
          size="small"
          variant="outlined"
        />
      )
    }
    if (ad.pep_action === 'rejected') {
      return (
        <Chip
          label="PEP Rejected"
          color="error"
          size="small"
          variant="outlined"
        />
      )
    }
    if (ad.pep_flagged) {
      return (
        <Chip
          label="PEP Flagged"
          color="warning"
          size="small"
          variant="outlined"
        />
      )
    }
    return null
  }

  // Customer risk rating — the screening engine writes customer_risk_score,
  // customer_risk_rating and a customer_risk_components breakdown onto
  // additional_details. Only policies scored by it carry the fields, so the
  // display is driven by their presence rather than by partner.
  const riskRating = ad.customer_risk_rating
  const riskScore = ad.customer_risk_score
  const riskComponents =
    ad.customer_risk_components &&
    typeof ad.customer_risk_components === 'object'
      ? ad.customer_risk_components
      : null
  // Heaviest contributor first: the breakdown is read to see what drove the
  // score, and the engine's own key order does not carry that.
  const riskComponentKeys = riskComponents
    ? Object.keys(riskComponents).sort(function(a, b) {
        return (
          (Number(riskComponents[b]) || 0) - (Number(riskComponents[a]) || 0)
        )
      })
    : []
  const hasCustomerRisk =
    isPresent(riskRating) ||
    isPresent(riskScore) ||
    riskComponentKeys.length > 0

  const riskRatingColor = () => {
    const rating = String(riskRating || '').toLowerCase()
    return RISK_RATING_COLORS[rating] || 'default'
  }

  const getCustomerRiskChip = () => {
    if (!isPresent(riskRating)) {
      return null
    }
    return (
      <Chip
        label={`Customer Risk: ${riskRating}`}
        color={riskRatingColor()}
        size="small"
        variant="outlined"
      />
    )
  }

  const premium =
    policy.quotation && policy.quotation.total_premium_amount_in_cents
      ? `${currencyCode} ${policy.quotation.total_premium_amount_in_cents /
          100} per ${policy.quotation.premium_cardinality} ${
          policy.quotation.premium_granularity
        }`
      : 'N/A'

  // omk details
  const scheduleEntries =
    policy.quotation && Array.isArray(policy.quotation.policy_schedule_entries)
      ? policy.quotation.policy_schedule_entries
      : []

  let principal = {}
  const principalEntry = scheduleEntries.find(function(entry) {
    return entry.dependant && entry.dependant.relationship === 'Principal'
  })
  if (principalEntry && principalEntry.dependant) {
    principal = principalEntry.dependant
  }

  // Declared medical conditions. The journeys record the condition names on the
  // member or once at policy level, and the yes/no answer separately
  // (dependant.additional_details.pre_existing_condition,
  // additional_details.medical_pre_condition), so all of those are consulted.
  // Passed as the LIST of every detail blob on the policy, not just the policy-level
  // one, so a condition name recorded on the quotation snapshot is still found
  // instead of falling through to "Declared — condition not specified" (item 3).
  const detailSources = policyDetailSources(policy)
  const principalConditions = getScheduleEntryMedicalConditions(
    principalEntry,
    detailSources
  )
  const policyLevelConditions = detailSources.reduce(
    (found, blob) => (found.length ? found : getMedicalConditions(blob)),
    []
  )
  const policyConditionDeclared = ad.medical_pre_condition === true
  const principalConditionsSummary = describeMedicalConditions(
    principalConditions.length ? principalConditions : policyLevelConditions,
    isMedicalConditionDeclaredForScheduleEntry(principalEntry, detailSources) ||
      policyConditionDeclared
  )

  // Backend uses the first beneficiary in the list as the next of kin.
  const nextOfKin =
    Array.isArray(policy.beneficiaries) && policy.beneficiaries.length > 0
      ? policy.beneficiaries[0]
      : {}
  // A minor beneficiary carries guardian and refund details under
  // additional_details; show them when present.
  const nextOfKinDetails = nextOfKin.additional_details || {}
  const guardian = nextOfKinDetails.guardian || {}
  const refundDetails = nextOfKinDetails.refund_details || {}
  const hasGuardian = Object.keys(guardian).length > 0
  const hasRefundDetails = Object.keys(refundDetails).length > 0

  const statusView = policyStatusLabel(policy)

  /**
   * What an amendment changed, so the authoriser sees the specific fields instead of
   * diffing the whole record by eye (retest item 6). The customer journey computes the
   * before/after and sends it as `amendment_changes`; where the backend has not kept
   * that key, the same summary is folded into `amendment_reason` as
   * `[Changed — Field: "a" → "b"; …]`, which is parsed back out here.
   */
  const amendmentChanges = (() => {
    const structured = readPolicyField(policy, 'amendment_changes')
    if (Array.isArray(structured) && structured.length) {
      return structured.map(c => ({
        field: c.field || c.label || '',
        from:
          c.from === undefined || c.from === null || c.from === ''
            ? '—'
            : c.from,
        to: c.to === undefined || c.to === null || c.to === '' ? '—' : c.to
      }))
    }
    const reason = readPolicyField(policy, 'amendment_reason')
    const match = /\[Changed — ([\s\S]*)\]\s*$/.exec(String(reason || ''))
    if (!match) {
      return []
    }
    return match[1]
      .split(';')
      .map(part => {
        const m = /^\s*(.+?):\s*"([\s\S]*?)"\s*→\s*"([\s\S]*?)"\s*$/.exec(part)
        return m ? { field: m[1], from: m[2] || '—', to: m[3] || '—' } : null
      })
      .filter(Boolean)
  })()

  const yesNo = value => (value ? 'Yes' : 'No')
  const orNA = value => (value === 0 || value ? value : 'N/A')
  // `policy.additional_details` is only one of the five places a captured field can
  // sit — an amended policy's richer snapshot lands on the quotation instead, and
  // some fields only ever reach the principal's own detail blob. Reading one place
  // was printing "N/A" over data we hold (retest item 5). `read` walks all of them.
  const read = keys => readPolicyField(policy, keys)

  const omkAdditionalDetails = [
    { label: 'Title', value: orNA(read(['title', 'salutation'])) },
    { label: 'Email', value: orNA(read('email')) },
    { label: 'Occupation', value: orNA(read('occupation')) },
    {
      label: 'Employer',
      value: orNA(read(['employer_name', 'employer', 'company_name']))
    },
    { label: 'Industry', value: orNA(read('industry')) },
    {
      label: 'Monthly Income',
      value: read(['monthly_income', 'gross_monthly_income', 'income'])
        ? `${currencyCode} ${read([
            'monthly_income',
            'gross_monthly_income',
            'income'
          ])}`
        : 'N/A'
    },
    { label: 'KRA PIN', value: orNA(read(['kra_pin', 'tax_number'])) },
    {
      label: 'Premium Amount',
      value:
        ad.premium_amount === 0 || ad.premium_amount
          ? `${currencyCode} ${ad.premium_amount}`
          : 'N/A'
    },
    {
      label: 'Payment Method',
      value: orNA(ad.payment_method || policy.payment_method)
    },
    { label: 'Estate', value: orNA(read(['estate', 'residential_area'])) },
    { label: 'Building', value: orNA(read(['building', 'building_name'])) },
    {
      label: 'Physical Address',
      value: orNA(read(['physical_address', 'street_address', 'address']))
    },
    {
      label: 'Country of Residence',
      value: orNA(read(['country_of_residence', 'residence_country']))
    },
    { label: 'Town', value: orNA(read(['town', 'city'])) },
    { label: 'Postal Code', value: orNA(read(['postal_code', 'post_code'])) },
    { label: 'P.O. Box', value: orNA(read(['po_box', 'postal_address'])) },
    { label: 'Kenyan Citizen', value: yesNo(read('is_kenyan_citizen')) },
    { label: 'Medical Pre-Condition', value: yesNo(ad.medical_pre_condition) },
    {
      label: 'Declared Medical Conditions',
      value: describeMedicalConditions(
        policyLevelConditions,
        policyConditionDeclared
      )
    },
    { label: 'Data Consent', value: yesNo(read('data_consent')) },
    { label: 'Marketing Consent', value: yesNo(read('marketing_consent')) },
    {
      label: 'Data Protection Consent',
      value: yesNo(read('data_protection_consent'))
    },
    {
      label: 'Requires Underwriter Endorsement',
      value: yesNo(ad.requires_underwriter_endorsement)
    },
    { label: 'Amendment Reason/Note', value: orNA(read('amendment_reason')) },
    { label: 'Agent Name', value: orNA(read('agent')) },
    { label: 'Agent Email', value: orNA(read('agent_email')) }
  ]

  return (
    <Box sx={{ p: 2 }}>
      {/* Policy Header */}
      <Card sx={{ mb: 2, boxShadow: 0, border: '1px solid #e2e8f0' }}>
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.5 }}>
                {policy.policy_number || 'N/A'}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {policy.product_name}{' '}
                {ad.package &&
                  `- ${ad.package.charAt(0).toUpperCase() +
                    ad.package.slice(1)}`}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center'
              }}
            >
              {getCustomerRiskChip()}
              {getAmlStatusChip()}
              {getPepStatusChip()}
              <Chip
                label={policy.active ? 'Active' : 'Inactive'}
                color={policy.active ? 'success' : 'default'}
                size="small"
                variant="outlined"
              />
              <IconButton
                aria-label="more"
                id="long-button"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={event => handleClick(event, policy)}
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
              {selectedPolicyAction &&
                selectedPolicyAction.guid === policy.guid && (
                  <Menu
                    id="long-menu"
                    MenuListProps={{
                      'aria-labelledby': 'long-button'
                    }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    PaperProps={{
                      style: {
                        maxHeight: ITEM_HEIGHT * 4.5,
                        width: '20ch'
                      }
                    }}
                  >
                    {!isAgentView &&
                      !['Non_Payment', 'Cancelled'].includes(policy.status) && (
                      <MenuItem
                        onClick={() => {
                          setAnchorEl(null)
                          if (getPartnerGuid() === 'omk') {
                            // OMK amends inside an embedded iframe that loads the
                            // policy from its policy_number. Pass it as a query
                            // param (not a path segment) so a number containing
                            // special characters survives the route matcher and
                            // reaches the amend screen intact — otherwise it is
                            // truncated and the operator is asked to re-type it.
                            hashHistory.push({
                              pathname: 'admin/amend-policy',
                              query: { policy_number: policy.policy_number }
                            })
                          } else {
                            props.setQuotationToReCreate(policy)
                          }
                        }}
                      >
                        Amend
                      </MenuItem>
                    )}
                    {!isAgentView &&
                      !['Discarded', 'Cancelled'].includes(policy.status) && (
                      <MenuItem
                        onClick={() => {
                          props.toggleUpdateBeneficaryDialog(policy)
                          setAnchorEl(null)
                        }}
                      >
                        Update Beneficiary
                      </MenuItem>
                    )}
                    {!isAgentView &&
                      ![
                        'Non_Payment',
                        'Cancelled',
                        'Rejected',
                        'Un_Confirmed'
                      ].includes(policy.status) &&
                      getPartnerGuid() !== 'old_mutual_malawi' && (
                      <MenuItem
                        style={{
                          display: hasAuthourity('CAPTURE_POLICY')
                            ? 'block'
                            : 'none'
                        }}
                        onClick={() => {
                          setAnchorEl(null)
                          props.toggleOpenCancelPolicyDialog()
                        }}
                      >
                        Cancel Policy
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null)
                        props.setPolicyToDownloadDocumentFor(policy)
                      }}
                    >
                      Download Policy Document
                    </MenuItem>
                  </Menu>
                )}
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Premium
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {premium}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Start Date
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatDateLocale(policy.start_date) || 'N/A'}
              </Typography>
            </Grid>
            {/* <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">Status</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{policy.status || "N/A"}</Typography>
            </Grid> */}
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Status
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {partnerGuid === 'omk'
                  ? statusView.label
                  : policy.status || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                {partnerGuid === 'omk' ? 'Cover Period' : 'Due Date'}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {partnerGuid === 'omk'
                  ? `${formatDateLocale(policy.start_date) ||
                      'N/A'} - ${formatDateLocale(
                      addYears(policy.start_date, 1)
                    ) || 'N/A'}`
                  : formatDateLocale(policy.due_date) || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {/* Member Information */}
        {partnerGuid === 'omk' ? (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 0, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Principal Member
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  {getFullName(principal) ||
                    getFullName(mainMember) ||
                    ad.principal_full_names ||
                    getFullName(ad) ||
                    'N/A'}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  ID: {principal.national_id || principal.passport || 'N/A'}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Gender
                    </Typography>
                    <Typography variant="body2">
                      {principal.gender || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      DOB
                    </Typography>
                    <Typography variant="body2">
                      {principal.date_of_birth || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Phone
                    </Typography>
                    <Typography variant="body2">
                      {principal.msisdn || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      National ID/Passport
                    </Typography>
                    <Typography variant="body2">
                      {principal.national_id || principal.passport || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Relationship
                    </Typography>
                    <Typography variant="body2">
                      {principal.relationship || 'Principal'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Typography variant="body2" color="textSecondary">
                      Declared Medical Conditions
                    </Typography>
                    <Typography variant="body2">
                      {principalConditionsSummary}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 0, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Principal Member
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  {mainMember.fullName || ad.principal_full_names || 'N/A'}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  ID: {mainMember.idNumber || ad.national_id || 'N/A'}
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Gender
                    </Typography>
                    <Typography variant="body2">
                      {mainMember.gender || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      DOB
                    </Typography>
                    <Typography variant="body2">
                      {mainMember.dateOfBirth || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Phone
                    </Typography>
                    <Typography variant="body2">
                      {mainMember.phoneNumber || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Email
                    </Typography>
                    <Typography variant="body2">
                      {mainMember.email || ad.email || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* What this amendment changed, field by field. Without it the authoriser had
            to compare the full record against memory to find the edit (retest item 6).
            The supporting document the change was made on is listed with the policy's
            other onboarding documents as "Amendment supporting document". */}
        {partnerGuid === 'omk' && amendmentChanges.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 0, border: '1px solid #FBA92D' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  Amendment — what changed
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  {readPolicyField(policy, 'amendment_reason')
                    ? String(
                        readPolicyField(policy, 'amendment_reason')
                      ).replace(/\s*\[Changed — [\s\S]*\]\s*$/, '')
                    : 'No reason was recorded.'}
                </Typography>
                {amendmentChanges.map((change, index) => (
                  <Box
                    key={change.field + index}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'baseline',
                      gap: 1,
                      py: 1,
                      borderBottom:
                        index === amendmentChanges.length - 1
                          ? 'none'
                          : '1px solid #edf2f7'
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ minWidth: 180 }}
                    >
                      {change.field}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ textDecoration: 'line-through', color: '#8a94a6' }}
                    >
                      {change.from}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#8a94a6' }}>
                      →
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        backgroundColor: '#FFF4DE',
                        px: 0.75
                      }}
                    >
                      {change.to}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* All members on the policy, with the medical conditions declared for
            each one. Skipped on a single-member policy, where the Principal
            Member card above already carries the same details. */}
        {partnerGuid === 'omk' && scheduleEntries.length > 1 && (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 0, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Members ({scheduleEntries.length})
                </Typography>

                {scheduleEntries.map((entry, index) => {
                  const member = entry.dependant || {}
                  return (
                    <Box
                      key={member.guid || index}
                      sx={{
                        pb: 2,
                        mb: 2,
                        borderBottom:
                          index === scheduleEntries.length - 1
                            ? 'none'
                            : '1px solid #edf2f7'
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        {getFullName(member) || 'N/A'}
                        {member.relationship ? ` — ${member.relationship}` : ''}
                      </Typography>
                      <Grid container spacing={1}>
                        {/* Gender and phone are read across every place they can sit,
                            not just the top-level dependant field — the engine drops
                            both off dependant records (retest item 4), so anything
                            that survived elsewhere on the policy is shown. */}
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            Gender
                          </Typography>
                          <Typography variant="body2">
                            {readMemberField(policy, member, [
                              'gender',
                              'sex'
                            ]) || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            DOB
                          </Typography>
                          <Typography variant="body2">
                            {member.date_of_birth || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            National ID/Passport
                          </Typography>
                          <Typography variant="body2">
                            {member.national_id || member.passport || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            Phone
                          </Typography>
                          <Typography variant="body2">
                            {readMemberField(policy, member, [
                              'msisdn',
                              'phone_number',
                              'phone',
                              'contact_number'
                            ]) || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">
                            Benefits
                          </Typography>
                          <Typography variant="body2">
                            {Array.isArray(entry.benefits) &&
                            entry.benefits.length
                              ? entry.benefits
                                  .map(function(benefit) {
                                    return `${
                                      benefit.type
                                    } (${currencyCode} ${(benefit.cover_amount_in_cents / 100).toLocaleString()})`
                                  })
                                  .join(', ')
                              : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">
                            Declared Medical Conditions
                          </Typography>
                          <Typography variant="body2">
                            {describeScheduleEntryMedicalConditions(
                              entry,
                              detailSources
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )
                })}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Next of Kin */}
        {!isAgentView && (
        <Grid item xs={12}>
          {partnerGuid === 'omk' ? (
            <Card sx={{ boxShadow: 0, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Next of Kin / Beneficiary
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  {/* full_name is checked because it is the only name field
                        the group-onboarding flow populates — it writes
                        full_name and leaves first/middle/last null, so
                        building from the parts alone yields a blank. */}
                  {getFullName(nextOfKin) ||
                    nextOfKin.full_name ||
                    ad.next_of_kin_full_name ||
                    policy.beneficiary_name ||
                    'N/A'}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  Relationship:{' '}
                  {nextOfKin.relationship ||
                    ad.next_of_kin_relationship ||
                    'N/A'}
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      National ID/Passport
                    </Typography>
                    <Typography variant="body2">
                      {nextOfKin.national_id ||
                        ad.next_of_kin_national_id ||
                        nextOfKin.passport ||
                        'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      DOB
                    </Typography>
                    <Typography variant="body2">
                      {nextOfKin.date_of_birth ||
                        policy.beneficiary_date_of_birth ||
                        'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Phone
                    </Typography>
                    <Typography variant="body2">
                      {nextOfKin.msisdn ||
                        ad.next_of_kin_phone_number ||
                        policy.beneficiary_msisdn ||
                        'N/A'}
                    </Typography>
                  </Grid>
                  {nextOfKinDetails.identification_type && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        ID Type
                      </Typography>
                      <Typography variant="body2">
                        {nextOfKinDetails.identification_type.replace(
                          /_/g,
                          ' '
                        )}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {/* Guardian — shown for a minor beneficiary */}
                {hasGuardian && (
                  <>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mt: 2, mb: 1 }}
                    >
                      Guardian
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          Full Name
                        </Typography>
                        <Typography variant="body2">
                          {getFullName(guardian) || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          Relationship
                        </Typography>
                        <Typography variant="body2">
                          {guardian.relationship || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          National ID/Passport
                        </Typography>
                        <Typography variant="body2">
                          {guardian.national_id || guardian.passport || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          Phone
                        </Typography>
                        <Typography variant="body2">
                          {guardian.phone_number || 'N/A'}
                        </Typography>
                      </Grid>
                      {guardian.identification_type && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            ID Type
                          </Typography>
                          <Typography variant="body2">
                            {guardian.identification_type.replace(/_/g, ' ')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}

                {/* Refund details */}
                {hasRefundDetails && (
                  <>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mt: 2, mb: 1 }}
                    >
                      Refund Details
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          Bank Name
                        </Typography>
                        <Typography variant="body2">
                          {refundDetails.bank_name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          Bank Branch
                        </Typography>
                        <Typography variant="body2">
                          {refundDetails.bank_branch || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          Account Name
                        </Typography>
                        <Typography variant="body2">
                          {refundDetails.account_name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          Account Number
                        </Typography>
                        <Typography variant="body2">
                          {refundDetails.account_number || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          M-Pesa Number
                        </Typography>
                        <Typography variant="body2">
                          {refundDetails.mpesa_number || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ boxShadow: 0, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Next of Kin
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  {nok.fullName || policy.beneficiary_name || 'N/A'}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  Relationship: {nok.relationship || 'N/A'}
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Gender
                    </Typography>
                    <Typography variant="body2">
                      {nok.gender || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      DOB
                    </Typography>
                    <Typography variant="body2">
                      {nok.dateOfBirth || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      ID
                    </Typography>
                    <Typography variant="body2">
                      {nok.idNumber || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Phone
                    </Typography>
                    <Typography variant="body2">
                      {nok.phoneNumber || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>
        )}

        {/* Additional Details (OMK) */}
        {partnerGuid === 'omk' && !isAgentView && (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 0, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Additional Details
                </Typography>

                <Grid container spacing={2}>
                  {omkAdditionalDetails.map(detail => (
                    <Grid item xs={6} md={3} key={detail.label}>
                      <Typography variant="body2" color="textSecondary">
                        {detail.label}
                      </Typography>
                      <Typography variant="body2">{detail.value}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Additional Information */}
        {partnerGuid === 'omk' ? null : (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 0, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Additional Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Agent
                    </Typography>
                    <Typography variant="body2">{ad.agent || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="textSecondary">
                      Terms Accepted
                    </Typography>
                    <Typography variant="body2">
                      {ad.termsAccepted ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="textSecondary">
                      Payment Method
                    </Typography>
                    <Typography variant="body2">
                      {policy.payment_method || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="textSecondary">
                      Created
                    </Typography>
                    <Typography variant="body2">
                      {formatDateLocale(policy.created_at) || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="textSecondary">
                      Balance
                    </Typography>
                    <Typography variant="body2">
                      {`${currencyCode} ${policy.balance_amount_in_cents /
                        100}` || '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Typography variant="body2" color="textSecondary">
                      Beneficiary Phone
                    </Typography>
                    <Typography variant="body2">
                      {policy.beneficiary_msisdn || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Medical Conditions
                    </Typography>
                    <Typography variant="body2">
                      {ad.medical_conditions
                        ? Array.isArray(ad.medical_conditions)
                          ? ad.medical_conditions
                              .map(camelToReadable)
                              .join(', ')
                          : camelToReadable(ad.medical_conditions)
                        : 'None'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Customer Risk Rating — shown above AML/PEP because it is the
            context a reviewer weighs those decisions against. */}
        {hasCustomerRisk && (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 0, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6">Customer Risk Rating</Typography>
                </Box>

                <Grid container spacing={2}>
                  {isPresent(riskRating) && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Rating
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {riskRating}
                      </Typography>
                    </Grid>
                  )}
                  {isPresent(riskScore) && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Risk Score
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {riskScore}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {riskComponentKeys.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 1 }}
                    >
                      Score Breakdown
                    </Typography>
                    <Grid container spacing={2}>
                      {riskComponentKeys.map(key => (
                        <Grid item xs={6} md={2} key={key}>
                          <Typography variant="body2" color="textSecondary">
                            {riskComponentLabel(key)}
                          </Typography>
                          <Typography variant="body2">
                            {isPresent(riskComponents[key])
                              ? riskComponents[key]
                              : 'N/A'}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* AML Information */}
        {(ad.aml_flagged || ad.aml_action || ad.aml_reason) && (
          <Grid item xs={12}>
            <Card
              sx={{
                boxShadow: 0,
                border: ad.aml_flagged
                  ? '1px solid #f56565'
                  : '1px solid #e2e8f0'
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2
                  }}
                >
                  <Typography variant="h6">AML Information</Typography>
                  {ad.aml_flagged && !useMakerChecks && (
                    <ButtonInc
                      onClick={() => takeAction('aml')}
                      variant="contained"
                      size="small"
                    >
                      Take Action
                    </ButtonInc>
                  )}
                </Box>

                <Grid container spacing={2}>
                  {ad.aml_action && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Action
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {ad.aml_action}
                      </Typography>
                    </Grid>
                  )}
                  {ad.aml_reason && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary">
                        Reason
                      </Typography>
                      <Typography variant="body2">{ad.aml_reason}</Typography>
                    </Grid>
                  )}
                  {ad.aml_approved_by && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Approved By
                      </Typography>
                      <Typography variant="body2">
                        {ad.aml_approved_by}
                      </Typography>
                    </Grid>
                  )}
                  {ad.aml_approved_at && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Approved At
                      </Typography>
                      <Typography variant="body2">
                        {formatDateLocale(ad.aml_approved_at) ||
                          ad.aml_approved_at}
                      </Typography>
                    </Grid>
                  )}
                  {ad.aml_rejected_by && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Rejected By
                      </Typography>
                      <Typography variant="body2">
                        {ad.aml_rejected_by}
                      </Typography>
                    </Grid>
                  )}
                  {ad.aml_rejected_at && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Rejected At
                      </Typography>
                      <Typography variant="body2">
                        {formatDateLocale(ad.aml_rejected_at) ||
                          ad.aml_rejected_at}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* PEP Information */}
        {(ad.pep_flagged || ad.pep_action || ad.pep_reason) && (
          <Grid item xs={12}>
            <Card
              sx={{
                boxShadow: 0,
                border: ad.pep_flagged
                  ? '1px solid #f56565'
                  : '1px solid #e2e8f0'
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2
                  }}
                >
                  <Typography variant="h6">PEP Information</Typography>
                  {ad.pep_flagged && !useMakerChecks && (
                    <ButtonInc
                      onClick={() => takeAction('pep')}
                      variant="contained"
                      size="small"
                    >
                      Take Action
                    </ButtonInc>
                  )}
                </Box>

                <Grid container spacing={2}>
                  {ad.pep_position && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Position
                      </Typography>
                      <Typography variant="body2">{ad.pep_position}</Typography>
                    </Grid>
                  )}
                  {formatPepMatchScore(ad.match_score) && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Match Score
                      </Typography>
                      <Typography variant="body2">
                        {formatPepMatchScore(ad.match_score)}
                      </Typography>
                    </Grid>
                  )}
                  {ad.pep_action && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Action
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {ad.pep_action}
                      </Typography>
                    </Grid>
                  )}
                  {ad.pep_reason && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary">
                        Reason
                      </Typography>
                      <Typography variant="body2">{ad.pep_reason}</Typography>
                    </Grid>
                  )}
                  {ad.pep_approved_by && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Approved By
                      </Typography>
                      <Typography variant="body2">
                        {ad.pep_approved_by}
                      </Typography>
                    </Grid>
                  )}
                  {ad.pep_approved_at && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Approved At
                      </Typography>
                      <Typography variant="body2">
                        {formatDateLocale(ad.pep_approved_at) ||
                          ad.pep_approved_at}
                      </Typography>
                    </Grid>
                  )}
                  {ad.pep_rejected_by && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Rejected By
                      </Typography>
                      <Typography variant="body2">
                        {ad.pep_rejected_by}
                      </Typography>
                    </Grid>
                  )}
                  {ad.pep_rejected_at && (
                    <Grid item xs={6} md={2}>
                      <Typography variant="body2" color="textSecondary">
                        Rejected At
                      </Typography>
                      <Typography variant="body2">
                        {formatDateLocale(ad.pep_rejected_at) ||
                          ad.pep_rejected_at}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Flagged Action Dialog (AML / PEP) */}
      <Dialog
        open={takeActionDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {actionType === 'pep' ? 'PEP Flagged Action' : 'AML Flagged Action'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
          {loadingAction ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight={120}
            >
              <Typography color="textSecondary" gutterBottom fontSize={18}>
                Processing...
              </Typography>
            </Box>
          ) : feedback ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight={120}
            >
              <Typography
                color={feedback.includes('error') ? 'error' : 'primary'}
                gutterBottom
                fontSize={18}
              >
                {feedback}
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography gutterBottom sx={{ mb: 3 }}>
                Please review the flagged user and select an action below:
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Reason for Action
                </Typography>
                <TextField
                  label="Please provide a detailed reason"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  fullWidth
                  margin="normal"
                  multiline
                  minRows={4}
                  disabled={loadingAction}
                  required
                  inputProps={{ maxLength: 300 }}
                  helperText={
                    !trimmedReason
                      ? 'Reason is required for both approval and rejection'
                      : `${reason.length}/300 characters`
                  }
                  error={!trimmedReason}
                  sx={{ mt: 1 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Supporting Documentation
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Upload a PDF document to support your decision (Required)
                </Typography>
                {existingSupportingDocument && !uploadedFile && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {`${supportingDocumentLabel} already attached to this policy. Upload another one to replace it.`}
                  </Alert>
                )}
                <Box
                  sx={{
                    border: hasSupportingDocument
                      ? '1px dashed #cbd5e0'
                      : '1px dashed #e53e3e',
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: '#f9f9f9'
                  }}
                >
                  <DropzoneS3Uploader
                    onFinish={handleFileUpload}
                    upload={uploadOptions}
                    s3Url={s3Url}
                    passChildrenProps={false}
                    maxSize={1024 * 1024 * 10}
                    accept=".pdf,application/pdf"
                    disabled={loadingAction}
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      background: 'transparent',
                      border: 'none',
                      cursor: loadingAction ? 'not-allowed' : 'pointer',
                      borderRadius: '8px'
                    }}
                  >
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <ProgressState
                        progress={uploadProgress}
                        errorMessage={uploadError}
                      />
                    </Box>
                  </DropzoneS3Uploader>
                  {uploadedFile && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: '#c6f6d5',
                        borderRadius: 1,
                        border: '1px solid #9ae6b4'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: '#22543d', fontWeight: 500 }}
                      >
                        File successfully uploaded: {uploadedFile.filename}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {!canDecide && (
                <Alert severity="warning">
                  {`This policy cannot be approved or rejected until ${missingRequirements.join(
                    ' and '
                  )}.`}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            justifyContent: feedback ? 'center' : 'flex-end'
          }}
        >
          {!loadingAction && !feedback && (
            <>
              <Button
                onClick={handleDialogClose}
                disabled={loadingAction}
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDecision('Reject')}
                variant="contained"
                color="error"
                disabled={loadingAction || !canDecide}
                sx={{ mr: 1 }}
              >
                Reject
              </Button>
              <Button
                onClick={() => handleDecision('Approve')}
                variant="contained"
                color="success"
                disabled={loadingAction || !canDecide}
              >
                Approve
              </Button>
            </>
          )}
          {feedback && !loadingAction && (
            <Button onClick={handleDialogClose} variant="contained">
              OK
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PolicyDetailsFlp
